// "View as customer" (D56): read-only, said in the page head, and logged. Staff first say why; every
// request then carries the reason, the API writes it to the admin audit log and the customer
// sees a staff view in their own audit log. Nothing here can write: the view routes are GETs.
import { PageHead, Section } from '@apsio/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import {
  type CustomerAuditEntry,
  type OrgDetail,
  viewAuditQuery,
  viewIssuesQuery,
  viewOrgQuery,
} from '../api/queries'
import { DataTable, FormWindow, ReasonField, StatusWord } from '../components/kit'
import { useCrumbs } from '../components/Shell'
import { Empty, ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { PLAN_LABEL } from './pills'

const KIND: Record<string, MessageId> = {
  crash: 'view.kind.crash',
  error: 'view.kind.error',
  anr: 'view.kind.anr',
  hang: 'view.kind.hang',
  abnormal_exit: 'view.kind.abnormalExit',
}

/** ".../Organizações/Acme/Ver como o cliente": the organization's name once known. */
function useViewCrumbs(orgId: string, name: string | undefined) {
  const { t } = useI18n()
  useCrumbs([...(name ? [{ label: name, to: `/orgs/${orgId}` }] : []), { label: t('view.title') }])
}

export function ViewAsPage() {
  const { orgId } = useParams({ strict: false }) as { orgId: string }
  const [reason, setReason] = useState('')
  // One of the two is mounted, so one sets the bar's crumbs.
  return reason ? (
    <CustomerView orgId={orgId} reason={reason} />
  ) : (
    <ReasonStep orgId={orgId} onStart={setReason} />
  )
}

/** Why staff look, before anything of the customer's is read. */
function ReasonStep({ orgId, onStart }: { orgId: string; onStart: (reason: string) => void }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [draft, setDraft] = useState('')
  // The name from the organization's page, if staff came from it: fetching it here would be one
  // more view in the customer's audit log.
  useViewCrumbs(orgId, useQueryClient().getQueryData<OrgDetail>(['org', orgId])?.name)

  function start(e: FormEvent) {
    e.preventDefault()
    if (draft.trim()) onStart(draft.trim())
  }

  return (
    <main id="main" className="pg">
      <PageHead
        crumb={<Link to="/orgs">{t('nav.orgs')}</Link>}
        title={t('view.title')}
        meta={t('view.readOnly')}
      />
      <FormWindow
        title={t('view.formTitle')}
        submitLabel={t('view.start')}
        onSubmit={start}
        onCancel={() => navigate({ to: '/orgs/$orgId', params: { orgId } })}
      >
        <ReasonField value={draft} onChange={setDraft} hint={t('view.why')} />
      </FormWindow>
    </main>
  )
}

/** The customer's data, read-only: the head says so, and leaving is its one action. */
function CustomerView({ orgId, reason }: { orgId: string; reason: string }) {
  const { t } = useI18n()
  const org = useQuery(viewOrgQuery(orgId, reason))
  const audit = useQuery(viewAuditQuery(orgId, reason))
  useViewCrumbs(orgId, org.data?.name)
  const leave = (
    <Link to="/orgs/$orgId" params={{ orgId }} className="btn">
      {t('view.leave')}
    </Link>
  )
  const crumb = <Link to="/orgs">{t('nav.orgs')}</Link>
  if (org.isPending)
    return (
      <main id="main" className="pg">
        <Loading />
      </main>
    )
  if (org.isError)
    return (
      <main id="main" className="pg">
        <PageHead crumb={crumb} title={t('view.title')} actions={leave} />
        <ErrorState error={org.error} retry={() => org.refetch()} />
      </main>
    )
  const o = org.data
  return (
    <main id="main" className="pg">
      <PageHead
        crumb={crumb}
        title={<span translate="no">{o.name}</span>}
        meta={
          <>
            <strong className="view-as">{t('view.banner')}</strong> {t(PLAN_LABEL[o.plan])} ·{' '}
            <StatusWord kind="org" status={o.status} /> · {t('view.readOnlyShort')}
          </>
        }
        actions={leave}
      />
      {o.projects.map((p) => (
        <ProjectIssues key={p.id} project={p} reason={reason} />
      ))}
      <Section title={t('view.audit')}>
        {audit.isPending ? (
          <Loading />
        ) : audit.isError ? (
          <ErrorState error={audit.error} retry={() => audit.refetch()} />
        ) : (
          <CustomerAudit entries={audit.data} />
        )}
      </Section>
    </main>
  )
}

function ProjectIssues({
  project,
  reason,
}: {
  project: OrgDetail['projects'][number]
  reason: string
}) {
  const { t, number, dateTime, locale } = useI18n()
  const issues = useQuery(viewIssuesQuery(project.id, reason))
  return (
    <Section title={<span translate="no">{project.name}</span>} meta={t('view.issues')}>
      {issues.isPending ? (
        <Loading />
      ) : issues.isError ? (
        <ErrorState error={issues.error} retry={() => issues.refetch()} />
      ) : issues.data.length === 0 ? (
        <Empty title={t('view.noIssues')} />
      ) : (
        <DataTable label={t('view.issues')} minWidth={560}>
          <thead>
            <tr>
              <th scope="col">{t('view.col.issue')}</th>
              <th scope="col">{t('view.col.kind')}</th>
              <th scope="col" className="num">
                {t('view.col.events')}
              </th>
              <th scope="col">{t('view.col.last')}</th>
            </tr>
          </thead>
          <tbody>
            {issues.data.map((i) => (
              <tr key={i.id}>
                <td translate="no">{i.type || i.message}</td>
                <td className={i.kind === 'crash' ? 'view-kind crash' : 'view-kind'}>
                  {t(KIND[i.kind] ?? 'view.kind.error').toLocaleLowerCase(locale)}
                </td>
                <td className="num">{number(i.occurrences)}</td>
                <td>{dateTime(`${i.last_seen.slice(0, 23)}Z`)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </Section>
  )
}

function CustomerAudit({ entries }: { entries: CustomerAuditEntry[] }) {
  const { t, dateTime } = useI18n()
  if (entries.length === 0) return <Empty title={t('audit.empty')} />
  return (
    <DataTable label={t('table.of', { name: t('view.audit') })} minWidth={480}>
      <thead>
        <tr>
          <th scope="col">{t('audit.col.when')}</th>
          <th scope="col">{t('view.col.actor')}</th>
          <th scope="col">{t('audit.col.action')}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id}>
            <td>{dateTime(e.created_at)}</td>
            <td>{e.actor_kind === 'staff' ? t('view.actor.staff') : t('view.actor.other')}</td>
            <td className="cell-mono" translate="no">
              {e.action}
            </td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  )
}
